import { notification } from 'antd5';
import { ModalActionButtons, Button } from 'components/ui';

import { IMAGE_AND_METADATA_UPLOAD_OPTIONS } from 'utils/constants';
import { FileUploader } from 'components/FileUploader';
import { useAccountContext } from 'utils/account/AccountContext';
import { useGetIngestUploadUrls } from 'utils/ingest';
import {
  useNotifyInspectionImageUploadSuccessMutation,
  useNotifyInspectionImageUploadErrorMutation,
} from 'types/atlas-graphql';
import { OnCompleteStatus } from 'components/FileUploader/Modal/FileUploadModal';

// So long as it's a `.mcap` file, we should be good
const isValidFilePath = (filename: string) => {
  return filename.toLowerCase().includes('.mcap');
};

interface SkyCrawlerUploadProps {
  inspectionId: string;
  onComplete: () => void;
  onBack: () => void;
}

export const SkyCrawlerUpload: React.FunctionComponent<SkyCrawlerUploadProps> = ({
  inspectionId,
  onComplete: onSuccessCallback,
  onBack,
}) => {
  const { customer } = useAccountContext();
  const organizationId = customer?.id;

  const [notifySuccess] = useNotifyInspectionImageUploadSuccessMutation();
  const [notifyError] = useNotifyInspectionImageUploadErrorMutation();
  const { cancelTokenSource, getIngestUploadUrls } = useGetIngestUploadUrls();

  const getUploadUrls = async (filePaths: any) => {
    if (!organizationId) {
      return undefined;
    }

    try {
      const { items } = await getIngestUploadUrls({
        recordId: inspectionId,
        dataType: IMAGE_AND_METADATA_UPLOAD_OPTIONS.SKYCRAWLER_LOG,
        files: filePaths || [],
      });

      return items;
    } catch (e) {
      return undefined;
    }
  };

  const onUploadComplete = () => {
    onSuccessCallback();
    notification.open({
      message: 'Inspection images currently processing.',
      description: 'You will receive a notification when your inspection has been processed.',
    });
  };

  const onComplete = ({ status }: { status: OnCompleteStatus }) => {
    if (status === 'CANCEL') {
      cancelTokenSource.cancel('Operation canceled by the user.');
      return;
    }
    if (status === 'SUCCESS') {
      onUploadComplete();
      notifySuccess({
        variables: {
          input: {
            inspectionId: inspectionId,
            message: `Your images and metadata have been successfully uploaded to your inspection with id "${inspectionId}".`,
          },
        },
      });
    }
    if (status === 'ERROR') {
      notifyError({
        variables: {
          input: {
            inspectionId: inspectionId,
            message: `An error occurred while uploading images and metadata to your inspection with id "${inspectionId}".`,
          },
        },
      });
    }
  };

  return (
    <div>
      <p>
        A SkyCrawler inspection must have a log with the .mcap file extension. Once the log file is
        uploaded, it will be processed and released automatically. Inspection images and data will
        not be manually editable after upload.
      </p>
      <ModalActionButtons>
        <Button data-testid="inspection-upload-cancel-btn" onClick={onBack}>
          Back
        </Button>
        <FileUploader
          id="skycrawler-data-upload"
          label={'Upload File'}
          acceptedFileTypes={'.mcap'}
          multiple={false}
          directory={false}
          validation={isValidFilePath}
          onFileComplete={() => {}}
          onComplete={onComplete}
          action={getUploadUrls}
          multipartProps={{
            recordId: inspectionId,
            outputDataType: IMAGE_AND_METADATA_UPLOAD_OPTIONS.SKYCRAWLER_LOG,
          }}
        />
      </ModalActionButtons>
    </div>
  );
};
